import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { setEquals } from '../lib/equality';
import { DEFAULT_LIST_PANEL_LAYOUT_GUARD, DEFAULT_LIST_PANEL_PREFERENCE } from '../types';

import type { StateCreator } from 'zustand';
import type {
  ListPanelCoreState,
  ListPanelLayoutGuardState,
  ListPanelPreferenceState,
  ListPanelStore,
} from '../types';

const STORE_NAME = 'LIST_PANEL';
const KEYS = {
  PUSH_SELECTION: `${STORE_NAME}/pushSelection`,
  CLEAR_SELECTION: `${STORE_NAME}/clearSelection`,
  SET_FOCUSED_ID: `${STORE_NAME}/setFocusedId`,
  SET_PREFERENCE: `${STORE_NAME}/setPreference`,
  SET_LAYOUT_GUARD: `${STORE_NAME}/setLayoutGuard`,
  RESET: `${STORE_NAME}/reset`,
};

const initialCoreState: ListPanelCoreState = {
  selectedIds: new Set<string>(),
  focusedId: null,
};

const initialPreferenceState: ListPanelPreferenceState = DEFAULT_LIST_PANEL_PREFERENCE;

const initialLayoutGuardState: ListPanelLayoutGuardState = DEFAULT_LIST_PANEL_LAYOUT_GUARD;

// Compute derived state from core state
function computeDerivedState(selectedIds: Set<string>) {
  const selectionCount = selectedIds.size;
  return {
    hasSelection: selectionCount > 0,
    selectionCount,
    isSingleSelection: selectionCount === 1,
    isMultiSelection: selectionCount > 1,
  };
}

const createListPanelSlice: StateCreator<ListPanelStore, [['zustand/devtools', never]], []> = (
  set,
  get,
) => ({
  ...initialCoreState,
  ...computeDerivedState(initialCoreState.selectedIds),
  ...initialPreferenceState,
  ...initialLayoutGuardState,

  pushSelection: (ids, replace = false) => {
    const { selectedIds: currentIds, enableMultiSelect } = get();

    let newIds: Set<string>;

    if (replace || !enableMultiSelect) {
      // Replace mode or single select mode
      newIds = new Set(enableMultiSelect ? ids : ids.slice(0, 1));
    } else {
      // Add to existing selection
      newIds = new Set(currentIds);
      for (const id of ids) {
        newIds.add(id);
      }
    }

    // Skip update if selection hasn't changed
    if (setEquals(currentIds, newIds)) {
      return;
    }

    const derived = computeDerivedState(newIds);

    set(
      state => ({
        ...state,
        selectedIds: newIds,
        ...derived,
        // Update focused ID to last selected item
        focusedId: ids.length > 0 ? ids[ids.length - 1] : state.focusedId,
      }),
      undefined,
      KEYS.PUSH_SELECTION,
    );
  },

  clearSelection: () => {
    const { selectedIds: currentIds } = get();

    // Skip if already empty
    if (currentIds.size === 0) {
      return;
    }

    const emptySet = new Set<string>();
    const derived = computeDerivedState(emptySet);

    set(
      state => ({
        ...state,
        selectedIds: emptySet,
        ...derived,
        focusedId: null,
      }),
      undefined,
      KEYS.CLEAR_SELECTION,
    );
  },

  setFocusedId: id => {
    set(state => ({ ...state, focusedId: id }), undefined, KEYS.SET_FOCUSED_ID);
  },

  setPreference: preference => {
    set(
      state => ({
        ...state,
        ...preference,
      }),
      undefined,
      KEYS.SET_PREFERENCE,
    );
  },

  setLayoutGuard: layoutGuard => {
    set(
      state => ({
        ...state,
        ...layoutGuard,
      }),
      undefined,
      KEYS.SET_LAYOUT_GUARD,
    );
  },

  reset: () => {
    const emptySet = new Set<string>();
    const derived = computeDerivedState(emptySet);

    set(
      {
        ...initialCoreState,
        ...derived,
        ...initialPreferenceState,
        ...initialLayoutGuardState,
      },
      undefined,
      KEYS.RESET,
    );
  },
});

/**
 * List Panel Store
 *
 * Manages selection state for list panels in split view:
 * - Core state: selectedIds (Set), focusedId
 * - Derived state: hasSelection, selectionCount, isSingleSelection, isMultiSelection
 * - Preference state: enableMultiSelect, persistSelection
 * - Layout guard state: minPanelWidth, maxPanelWidth, defaultPanelWidth
 *
 * Uses Set for selectedIds to prevent unnecessary updates when selection hasn't changed.
 */
export const useListPanelStore = create<ListPanelStore>()(
  devtools(
    (...args) => ({
      ...createListPanelSlice(...args),
    }),
    {
      name: STORE_NAME,
    },
  ),
);
